import { createTheme } from "@material-ui/core/styles";
import {
  breakpoint,
  color,
  control,
  cssVariables,
  font,
  fontSize,
  iconSize,
  layout,
  radius,
  shadow,
  space
} from "./tokens";

const px = n => `${n}px`;
// Explicit media queries for the redesign breakpoints. Prefer these over
// theme.breakpoints.down(): in MUI v4 down("md") means "below lg" (<1320),
// which is easy to misread.
const media = {
  phone: `@media (max-width:${breakpoint.sm - 0.05}px)`, // <720
  belowDesktop: `@media (max-width:${breakpoint.md - 0.05}px)`, // <1100
  // hover effects only for real pointers (no sticky hover after a tap)
  hover: "@media (hover: hover) and (pointer: fine)",
  tablet: `@media (min-width:${
    breakpoint.sm
  }px) and (max-width:${breakpoint.md - 0.05}px)`,
  tabletUp: `@media (min-width:${breakpoint.sm}px)`, // ≥720
  desktop: `@media (min-width:${breakpoint.md}px)`, // ≥1100
  wide: `@media (min-width:${breakpoint.wide}px)`, // ≥1500
  xwide: `@media (min-width:${breakpoint.xwide}px)` // ≥1720
};
const mobile = media.phone;

const containerBase = {
  width: "100%",
  maxWidth: layout.container,
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: layout.gutter,
  paddingRight: layout.gutter,
  boxSizing: "border-box",
  [mobile]: {
    paddingLeft: layout.gutterMobile,
    paddingRight: layout.gutterMobile
  }
};

const pageBase = {
  ...containerBase,
  paddingTop: layout.pageTop,
  display: "flex",
  flexDirection: "column",
  gap: layout.sectionGap,
  minWidth: 0,
  [mobile]: {
    paddingLeft: layout.gutterMobile,
    paddingRight: layout.gutterMobile,
    gap: 36
  }
};

const textStyle = (size, weight, extra = {}) => ({
  fontFamily: font.family,
  fontSize: px(size),
  fontWeight: weight,
  ...extra
});

export const mainThemeObject = {
  breakpoints: {
    keys: ["xs", "sm", "md", "lg", "xl"],
    values: {
      xs: breakpoint.xs,
      sm: breakpoint.sm,
      md: breakpoint.md,
      lg: breakpoint.lg,
      xl: breakpoint.xl
    }
  },
  // Redesign tokens, available to every style file as theme.tokens / theme.layout
  tokens: { color, font, fontSize, iconSize, space, radius, shadow, control },
  layout,
  media,
  mixins: {
    hairline: { boxShadow: shadow.hairline },
    container: containerBase,
    containerWide: { ...containerBase, maxWidth: layout.containerWide },
    // Rebuilt pages: container + page-top padding + 48px section rhythm.
    page: pageBase,
    // Browse pages (home, listings, search, brands): same, on the wide container.
    pageWide: { ...pageBase, maxWidth: layout.containerWide },
    toolbar: {
      minHeight: 56,
      "@media (min-width:0px) and (orientation: landscape)": {
        minHeight: 48
      },
      "@media (min-width:600px)": {
        minHeight: 64
      }
    }
  },
  shadows: [
    "none",
    shadow.hairline,
    shadow.hairline,
    shadow.float,
    shadow.float,
    ...Array(20).fill(shadow.overlay)
  ],
  direction: "ltr",
  overrides: {
    MuiCssBaseline: {
      "@global": {
        ":root": cssVariables,
        html: {
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale"
        },
        body: {
          color: color.ink,
          backgroundColor: color.bg,
          fontFamily: font.family,
          fontSize: px(fontSize.body)
        },
        "a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible, [tabindex]:not([tabindex='-1']):focus-visible": {
          outline: `2px solid ${color.brand}`,
          outlineOffset: 2
        }
      }
    },
    MuiButton: {
      root: {
        minHeight: control.md,
        padding: "0 22px",
        borderRadius: radius.pill,
        fontSize: px(fontSize.body),
        fontWeight: 700,
        lineHeight: 1.2
      },
      sizeSmall: {
        minHeight: control.sm,
        padding: "0 16px",
        fontSize: px(14)
      },
      sizeLarge: {
        minHeight: control.lg,
        padding: "0 28px",
        fontSize: px(fontSize.body)
      },
      contained: {
        boxShadow: "none",
        backgroundColor: color.field,
        color: color.ink,
        "&:hover": {
          boxShadow: "none",
          backgroundColor: color.fieldHover
        },
        "&:active": { boxShadow: "none" },
        "&$disabled": {
          backgroundColor: color.field,
          color: color.muted,
          opacity: 0.6
        }
      },
      containedPrimary: {
        backgroundColor: color.ink,
        color: color.bg,
        "&:hover": { backgroundColor: color.inkHover }
      },
      outlined: {
        padding: "0 22px",
        border: "none",
        boxShadow: `inset 0 0 0 1px ${color.lineStrong}`,
        "&:hover": {
          backgroundColor: "transparent",
          boxShadow: shadow.hairlineHover
        }
      },
      outlinedPrimary: {
        border: "none",
        color: color.ink,
        "&:hover": {
          border: "none",
          backgroundColor: "transparent"
        }
      },
      outlinedSizeSmall: { padding: "0 16px" },
      outlinedSizeLarge: { padding: "0 28px" },
      text: { padding: "0 16px" },
      textPrimary: {
        color: color.ink,
        "&:hover": { backgroundColor: color.field }
      }
    },
    MuiIconButton: {
      root: {
        color: color.ink,
        "&:hover": { backgroundColor: color.field }
      }
    },
    MuiChip: {
      root: {
        height: control.sm,
        borderRadius: radius.pill,
        backgroundColor: color.field,
        color: color.ink,
        fontSize: px(14),
        fontWeight: 700
      },
      label: { paddingLeft: 16, paddingRight: 16 },
      colorPrimary: { backgroundColor: color.ink, color: color.bg },
      clickable: {
        "&:hover, &:focus": { backgroundColor: color.fieldHover }
      }
    },
    MuiCard: {
      root: {
        borderRadius: radius.card,
        boxShadow: shadow.hairline
      }
    },
    MuiCardMedia: {
      root: {
        height: "auto"
      }
    },
    MuiAppBar: {
      root: {
        boxShadow: "none"
      },
      colorPrimary: {
        backgroundColor: color.bg,
        color: color.ink
      }
    },
    MuiDivider: {
      root: {
        margin: "8px 0px",
        backgroundColor: color.line
      }
    },
    MuiDrawer: {
      paper: {
        backgroundColor: color.bg
      }
    },
    MuiPaper: {
      root: {
        boxShadow: "none",
        color: color.ink
      },
      rounded: {
        borderRadius: radius.card
      },
      elevation1: { boxShadow: "none" },
      elevation2: { boxShadow: "none" },
      elevation8: { boxShadow: shadow.overlay },
      elevation24: { boxShadow: shadow.overlay }
    },
    MuiDialog: {
      paper: {
        borderRadius: radius.dialog
      }
    },
    MuiBackdrop: {
      root: {
        backgroundColor: color.backdrop
      }
    },
    MuiPickersModal: {
      dialogRoot: {
        padding: 0
      }
    },
    MuiPopover: {
      paper: {
        padding: 0
      }
    },
    MuiMenu: {
      paper: {
        borderRadius: radius.card,
        boxShadow: `${shadow.overlay}, ${shadow.hairline}`
      }
    },
    MuiFilledInput: {
      root: {
        borderRadius: radius.field,
        backgroundColor: color.field,
        "&:hover": { backgroundColor: color.fieldHover },
        "&$focused": {
          backgroundColor: color.field,
          boxShadow: shadow.selected
        }
      },
      underline: {
        "&:before, &:after": { display: "none" }
      }
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: radius.field
      }
    },
    MuiFormControl: {
      root: {
        "& .MuiFormLabel-root": {
          color: `${color.muted} !important`
        }
      }
    },
    MuiRadio: {
      colorPrimary: { "&$checked": { color: color.ink } }
    },
    MuiCheckbox: {
      colorPrimary: { "&$checked": { color: color.ink } }
    },
    MuiLinearProgress: {
      colorPrimary: { backgroundColor: color.brandTint },
      barColorPrimary: { backgroundColor: color.brand }
    },
    MuiCircularProgress: {
      colorPrimary: { color: color.ink }
    }
  },
  transitions: {
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)"
    },
    duration: {
      standard: 300,
      short: 250,
      enteringScreen: 225,
      shorter: 200,
      leavingScreen: 195,
      shortest: 150,
      complex: 375
    }
  },
  typography: {
    fontFamily: font.family,
    fontSize: fontSize.ui, // MUI base size for its rem maths
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 800,
    h1: textStyle(fontSize.h1, 800, {
      letterSpacing: "-0.01em",
      lineHeight: 1.2,
      color: color.ink,
      [mobile]: { fontSize: px(fontSize.mobile.h1) }
    }),
    h2: textStyle(fontSize.h2, 800, {
      letterSpacing: "-0.01em",
      lineHeight: 1.25,
      color: color.ink,
      [mobile]: { fontSize: px(fontSize.mobile.h2) }
    }),
    h3: textStyle(fontSize.h3, 800, { lineHeight: 1.3, color: color.ink }),
    h4: textStyle(fontSize.title, 700, { lineHeight: 1.35, color: color.ink }),
    h5: textStyle(fontSize.body, 700, { lineHeight: 1.4, color: color.ink }),
    h6: textStyle(fontSize.body, 600, { lineHeight: 1.4, color: color.ink }),
    subtitle1: textStyle(fontSize.body, 600, { color: color.ink }),
    subtitle2: textStyle(fontSize.ui, 600, { color: color.ink }),
    body1: textStyle(fontSize.body, 400, { lineHeight: 1.5, color: color.ink }),
    body2: textStyle(fontSize.small, 400, {
      lineHeight: 1.45,
      color: color.muted
    }),
    caption: textStyle(fontSize.micro, 400, { color: color.muted }),
    overline: textStyle(fontSize.micro, 700, {
      color: color.muted,
      textTransform: "none",
      letterSpacing: 0
    }),
    button: textStyle(fontSize.body, 700, {
      textTransform: "none",
      letterSpacing: 0
    })
  },
  zIndex: {
    modal: 1300,
    snackbar: 1400,
    drawer: 1200,
    appBar: 1100,
    mobileStepper: 1000,
    tooltip: 1500
  },
  shape: {
    borderRadius: radius.field
  },
  props: {
    MuiButton: { disableElevation: true },
    MuiButtonBase: { disableRipple: true }
  },
  palette: {
    tonalOffset: 0.2,
    contrastThreshold: 3,
    type: "light",
    background: {
      paper: color.bg,
      default: color.bg
    },
    grey: {
      "50": "#FAFAFA",
      "100": color.surface,
      "200": color.field,
      "300": color.line,
      "400": color.lineStrong,
      "500": "#9E9E9E",
      "600": color.muted,
      "700": "#4D4D4D",
      "800": color.text2,
      "900": color.ink,
      A100: color.lineStrong,
      A200: "#AAAAAA",
      A400: color.text2,
      A700: "#4D4D4D"
    },
    text: {
      primary: color.ink,
      secondary: color.muted,
      disabled: "rgba(17, 17, 17, 0.38)",
      hint: color.muted
    },
    divider: color.line,
    // Black acts: primary is ink. Orange is a brand accent, see palette.brand.
    primary: {
      main: color.ink,
      light: color.inkHover,
      dark: "#000000",
      contrastText: color.bg
    },
    // Old secondary green → success green (savings, free delivery)
    secondary: {
      main: color.success,
      light: color.successTint,
      dark: "#0A5C2E",
      contrastText: color.bg
    },
    brand: {
      main: color.brand,
      text: color.brandText,
      tint: color.brandTint
    },
    deal: {
      main: color.deal,
      contrastText: color.bg
    },
    success: {
      main: color.success,
      light: color.successTint,
      dark: "#0A5C2E",
      contrastText: color.bg
    },
    error: {
      light: color.dangerTint,
      main: color.danger,
      dark: color.dangerHover,
      contrastText: color.bg
    },
    action: {
      hoverOpacity: 0.04,
      hover: color.surface,
      selected: color.field,
      disabledBackground: color.field,
      disabled: "rgba(17, 17, 17, 0.38)",
      active: color.ink
    }
  },
  themeName: "GrocerApp Theme 2026"
};

const MainTheme = createTheme(mainThemeObject);

export default MainTheme;
